import moment from "moment";
import BootstrapTable from "react-bootstrap-table-next";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import filterFactory, {
  selectFilter,
  textFilter,
} from "react-bootstrap-table2-filter";
import paginationFactory from "react-bootstrap-table2-paginator";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import { AiFillEye } from "react-icons/ai";
import { ImDownload3 } from "react-icons/im";
import { MdComment } from "react-icons/md";
import Swal from "sweetalert2";
import Pdf from "../../../../assets/icons/pdf.png";
import {
  getDateFromString,
  getFactureDate,
} from "../../../../redux/slice/invoiceSlice";
import { useAppSelector } from "../../../../redux/store";
import { InvoiceData } from "../../../../redux/types";
import { setInvoicesTraiterAsync } from "../../../../utils/api/invoice.api";
import { formatAmout } from "../../../../utils/formatter";
import { useDefaultPaginationOptions } from "../../../guest/hooks";
import CommentFactureModal from "../../../modal/comptabilite/CommentFactureModal";
import DetailFactureComptabiliteModal from "../../../modal/comptabilite/DetailFactureComptabiliteModal";
import ListeFactureComtabiliteTableSkeleton from "./ListeFactureComtabiliteTableSkeleton";
import {
  DEPENSE_BCS_FILTER_OPTIONS,
  getDepenseBcsInfo,
  HORS_BCS,
} from "../../../../utils/bcs-status";
import { useDocumentsDownload } from "../../../../utils/download-documents";
import { useRowSelection } from "../../../../utils/useRowSelection";
import BcsStatusBadge from "../../../common/BcsStatusBadge";
import SelectionBar from "../../../common/SelectionBar";

export function useCategories(invoices: InvoiceData[]) {
  const categories = useAppSelector((s) => s.categories);
  const getCat = () => {
    const newCat: { [name: string]: string } = {};
    const ids = Array.from(
      new Set(invoices.map((c) => c.categorie).filter((i) => !!i))
    ) as string[];
    for (let id of ids) {
      newCat[id] = categories.find((c) => c.id === id)?.name ?? "";
    }
    return newCat;
  };

  return getCat();
}

function ListeFactureComtabiliteTable({
  invoices,
  isLoading,
  reload,
  userId,
}: {
  invoices: InvoiceData[];
  isLoading: boolean;
  reload: () => any;
  userId: string;
}) {
  const cats = useCategories(invoices);

  // --- Sélection multiple : traitement comptable groupé et téléchargement --
  // Évite d'ouvrir la fenêtre de détail facture par facture pour cocher
  // « traité » ou récupérer le PDF.
  const { selected, selectedRows, selectRow, clear } =
    useRowSelection(invoices);
  const { isDownloading, download } = useDocumentsDownload();

  // Un seul document : le PDF. Plusieurs : une archive zip.
  const downloadSelection = async () => {
    await download(
      selectedRows.map((i) => ({
        // Les dépenses anciennes n'ont qu'une image dans `uri`.
        url: i.pdf || i.uri,
        name: `${getDateFromString(i.date, i.created_at).format(
          "YYYY-MM-DD"
        )} ${i.commerce_name ?? ""}`,
      })),
      `factures-depenses-${moment().format("YYYY-MM-DD")}`
    );
    // Comme après un marquage : la sélection est consommée par l'action.
    clear();
  };

  const applyTraiter = async (traiter: boolean) => {
    // On n'écrit que ce qui change : une facture déjà dans l'état demandé
    // n'est pas réécrite, et le compte annoncé est exact.
    const targets = selectedRows
      .filter((i) => !!i.traiter !== traiter)
      .map((i) => i.uid);
    const label = traiter ? "traitées" : "à traiter";
    if (targets.length === 0) {
      await Swal.fire({
        icon: "info",
        title: `Les factures sélectionnées sont déjà ${label}.`,
        iconColor: "#02B4C0",
        confirmButtonColor: "#02B4C0",
      });
      return;
    }
    const n = targets.length;
    const result: any = await Swal.fire({
      title: `Marquer ${n} facture${n > 1 ? "s" : ""} comme ${label} ?`,
      text:
        n < selectedRows.length
          ? `${selectedRows.length - n} autre(s) de la sélection le sont déjà.`
          : undefined,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "OUI",
      cancelButtonText: "NON",
      showLoaderOnConfirm: true,
      iconColor: "#02B4C0",
      confirmButtonColor: "#02B4C0",
      preConfirm: () =>
        setInvoicesTraiterAsync(targets, traiter)
          .then(() => true)
          .catch(() => {
            Swal.showValidationMessage(
              "La mise à jour a échoué, aucune facture n'a été modifiée."
            );
            return false;
          }),
      allowOutsideClick: () => !Swal.isLoading(),
    });
    if (!result?.value) return;
    clear();
    await reload();
    await Swal.fire({
      icon: "success",
      title: `${n} facture${n > 1 ? "s" : ""} marquée${n > 1 ? "s" : ""} comme ${label}`,
      iconColor: "#02B4C0",
      showConfirmButton: false,
      timer: 2000,
    });
  };

  const actionFormatter = (cell, row) => {
    return (
      <>
        <div className="table-actions-btn-container-commandes d-flex gap-3">
          <div className="container-btn-action-icon-modal position-relative">
            <button
              className="btn btn-action-icon-chat with-tooltip"
              data-tooltip-content="Commentaire"
              data-bs-toggle="modal"
              data-bs-target={`#CommentFactureModal${row.uid}`}
              id={`bntFactureModal${row.uid}`}
            >
              <MdComment />
            </button>
            <div className="statut-comment-btn">
              {row?.comments ? row?.comments?.length : 0}
            </div>
          </div>
          <div
            className="modal fade"
            id={`CommentFactureModal${row.uid}`}
            aria-labelledby="CommentFactureModalLabel"
            aria-hidden="true"
          >
            <CommentFactureModal
              userId={userId}
              facture={row}
              func={reload}
              invoiceId={row.uid}
              modalId={`CommentFactureModal${row.uid}`}
              btnId={`bntFactureModal${row.uid}`}
            />
          </div>
          <div
            className="modal fade"
            id={`DetailFactureComptabiliteModal${row.uid}`}
            aria-labelledby="DetailFactureComptabiliteModalLabel"
            aria-hidden="true"
          >
            <DetailFactureComptabiliteModal
              facture={row}
              func={reload}
              modalId={`DetailFactureComptabiliteModal${row.uid}`}
              btnId={`bntFactureComptabiliteModal${row.uid}`}
            />
          </div>

          <div className="container-btn-action-icon-modal">
            <button
              className="btn btn-action-modal-icon with-tooltip"
              data-tooltip-content="Voir"
              data-bs-toggle="modal"
              data-bs-target={`#DetailFactureComptabiliteModal${row?.uid}`}
              id={`bntFactureComptabiliteModal${row.uid}`}
            >
              <AiFillEye />
            </button>
          </div>
        </div>
      </>
    );
  };

  const statutFormatter = (cell, row) => {
    if (row?.traiter) {
      return (
        <div className="table-actions-btn-container-statut-facture">
          <p className="statut-facture-valide mb-0">Validé</p>
        </div>
      );
    } else {
      return (
        <div className="table-actions-btn-container-statut-facture">
          <p className="statut-facture-a-traiter mb-0">À traiter</p>
        </div>
      );
    }
  };

  const imgFormatter = (cell, row) => {
    return (
      <div className="table-actions-btn-container-img gap-2 d-flex align-items-center">
        <img
          src={Pdf}
          alt="type document"
          className="fast-facture-img-document-pdf"
        />{" "}
        <span className="ps-2">{row?.commerce_name}</span>
      </div>
    );
  };

  const dateFormatter = (cell, row) => {
    return (
      <div className="table-actions-btn-container-img gap-2 d-flex align-items-center">
        <span className="ps-2">
          {getDateFromString(row.date, row.created_at).format("DD/MM/YYYY")}{" "}
        </span>
      </div>
    );
  };

  const options = useDefaultPaginationOptions();

  const STATUS_OPTIONS = {
    0: "À traiter",
    1: "Validé",
  };

  const columns = [
    {
      dataField: "commerce_name",
      text: "Facture",
      formatter: (cell: any, row: any) => imgFormatter(cell, row),
      filter: textFilter({ placeholder: "Chercher..." }),
    },
    // En-têtes courts : la place gagnée profite au libellé de la facture et
    // au statut de facturation électronique.
    {
      dataField: "montant_ht",
      text: "HT",
      formatter: formatAmout,
    },
    {
      dataField: "montant_ttc",
      text: "TTC",
      formatter: formatAmout,
      filter: textFilter({ placeholder: "Chercher..." }),
    },
    {
      dataField: "date",
      text: "Date ",
      filter: textFilter({ placeholder: "Chercher..." }),
      sort: true,
      sortFunc: (a, b, order, dataField, rowA, rowB) => {
        if (order === "desc") {
          return getFactureDate(rowB) - getFactureDate(rowA);
        }
        return getFactureDate(rowA) - getFactureDate(rowB);
      },
      formatter: (cell: any, row: any) => dateFormatter(cell, row),
    },
    {
      dataField: "traiter",
      text: "Statut",
      filterValue: (cell) => (cell ? 1 : 0),
      filter: selectFilter({
        options: STATUS_OPTIONS,
        placeholder: "Choisir",
      }),
      formatter: (cell: any, row: any) => statutFormatter(cell, row),
    },
    {
      // Distinct de « Statut » (traitement comptable) : statut de la facture
      // reçue sur la plateforme de facturation électronique.
      dataField: "bcsLifecycleStatus",
      text: "Facturation électronique",
      filterValue: (cell: any, row: any) =>
        getDepenseBcsInfo(row)?.label ?? HORS_BCS,
      filter: selectFilter({
        options: DEPENSE_BCS_FILTER_OPTIONS,
        placeholder: "Choisir",
      }),
      formatter: (cell: any, row: any) => (
        <BcsStatusBadge info={getDepenseBcsInfo(row)} />
      ),
    },
    {
      dataField: "categorie",
      text: "Catégorie",
      filter: selectFilter({
        options: cats,
        placeholder: "Choisir",
      }),
      formatter: (cell: any, row: any) => {
        const cat = cats[cell] ?? "";
        row.cat = cat;
        return cat;
      },
    },
    {
      dataField: "id",
      text: "Actions",
      style: { textAlign: "center" },
      formatter: (cell: any, row: any) => actionFormatter(cell, row),
    },
  ];

  return (
    <>
      {isLoading && <ListeFactureComtabiliteTableSkeleton />}
      {!isLoading && (
        <>
          <SelectionBar count={selected.length} onClear={clear}>
            <button
              type="button"
              className="btn btn-see-more"
              onClick={() => applyTraiter(true)}
            >
              Marquer comme traitées
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => applyTraiter(false)}
            >
              Marquer comme à traiter
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={downloadSelection}
              disabled={isDownloading}
            >
              <ImDownload3 />
              <span className="ps-2">
                {selected.length > 1 ? "Télécharger (zip)" : "Télécharger"}
              </span>
            </button>
          </SelectionBar>
          <BootstrapTable
            keyField="uid"
            data={invoices}
            columns={columns}
            bordered={true}
            sort={{ dataField: "date", order: "desc" }}
            condensed={false}
            responsive={true}
            wrapperClasses="table-responsive admin-table"
            noDataIndication={() => "Il ya pas de données"}
            pagination={paginationFactory(options)}
            filter={filterFactory()}
            selectRow={selectRow}
          />
        </>
      )}
    </>
  );
}

export default ListeFactureComtabiliteTable;
